import { NgModule } from '@angular/core';
import { RouterModule, Routes, UrlSegment } from '@angular/router';
import { HideLoaderGuard } from '@qtek/libs/app-component';
import { ITEM_DIALOG_CONTEXT } from '@qtek/libs/analytics';
import { PermissionGuard } from '@qtek/libs/permissions';

import { dialogRoutes } from '@qtek/libs/dialog-routes';
import { AuthGuard, IconReadyGuard, LayoutComponent } from '@qtek/libs/layout';

export function caseInsensitiveMatcher(path: string, url: UrlSegment[]) {
  return url.length === 1 && url[0].path.toLowerCase() === path.toLowerCase()
    ? { consumed: url }
    : null;
}

const routes: Routes = [
  {
    path: 'about',
    loadChildren: () => import('@qtek/feature/about').then(m => m.AboutModule),
    canActivate: [HideLoaderGuard],
  },
  {
    path: 'auth',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@qtek/libs/auth-user').then(c => c.AuthRedirectComponent),
      },
      {
        path: '**',
        redirectTo: '',
      },
    ],
  },
  {
    // Every invitation link from `InvitationLinkService` (plain invite or CRM accept/decline)
    // shares this one path — resolved client-side: decode the token, then go straight to its
    // target if already logged in, or via /auth if not. InvRedirectComponent branches on the
    // token's `response` claim, not on the URL, to tell the two flows apart.
    path: 'inv/:token',
    loadComponent: () =>
      import('@qtek/feature/invitation-redirect').then(
        c => c.InvRedirectComponent
      ),
  },
  {
    path: 'orgs/impersonate',
    loadComponent: () =>
      import('@qtek/feature/organizations').then(
        m => m.OrganizationImpersonateComponent
      ),
  },
  {
    path: 'tpl-form',
    canActivate: [HideLoaderGuard],
    children: [
      {
        path: '',
        loadChildren: () =>
          import('@qtek/feature/template-form').then(m => m.TemplateFormModule),
        pathMatch: 'full',
      },
      {
        path: '**',
        redirectTo: '',
      },
    ],
  },
  {
    path: 'dialog',
    children: dialogRoutes,
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [AuthGuard, IconReadyGuard],
    children: [
      {
        path: '',
        redirectTo: 'dash',
        pathMatch: 'full',
      },
      {
        path: 'dash',
        loadChildren: () =>
          import('@qtek/feature/dashboard').then(m => m.DashboardModule),
        data: {
          breadcrumb: { label: 'GNR_DSH', translate: true },
        },
        pathMatch: 'full',
        canActivate: [PermissionGuard],
      },
      {
        path: 'stp',
        loadChildren: () =>
          import('@qtek/feature/setup').then(m => m.SetupModule),
        data: { breadcrumb: { label: 'GNR_SETUP', translate: true } },
      },
      {
        path: 'rels',
        loadChildren: () =>
          import('@qtek/feature/relations').then(m => m.RelationsModule),
      },
      {
        path: 'tasks',
        loadChildren: () => import('@qtek/libs/tasks').then(m => m.TasksModule),
        data: { breadcrumb: { label: 'GNR_TASKS', translate: true } },
        canActivate: [PermissionGuard],
      },
      {
        path: 'itms',
        loadChildren: () =>
          import('@qtek/feature/items').then(m => m.ItemsModule),
        canActivate: [PermissionGuard],
      },
      {
        path: 'deal/:itemId',
        providers: [
          { provide: ITEM_DIALOG_CONTEXT, useValue: 'sales-pipeline' },
        ],
        loadChildren: () =>
          import('@qtek/libs/item-dialog').then(m => m.ItemDialogModule),
        canActivate: [PermissionGuard],
      },
      {
        path: 'invitations',
        loadChildren: () =>
          import('@qtek/feature/deal-book').then(m => m.DealBookModule),
        canActivate: [PermissionGuard],
      },
      {
        path: 'tools',
        loadChildren: () =>
          import('@qtek/feature/tools').then(m => m.ToolsModule),
        data: { breadcrumb: { label: 'GNR_TOOLS', translate: true } },
        canActivate: [PermissionGuard],
      },
      {
        path: 'report',
        loadChildren: () =>
          import('@qtek/feature/report').then(m => m.ReportModule),
        data: { breadcrumb: { label: 'GNR_REPORTS', translate: true } },
        canActivate: [PermissionGuard],
      },
      {
        path: 'benchmark',
        loadChildren: () =>
          import('@qtek/feature/benchmark').then(m => m.benchmarkRoutes),
        data: { breadcrumb: { label: 'GNU_BENCHMARK', translate: true } },
        canActivate: [PermissionGuard],
      },
      {
        // SD-2999: the embedded survey results (V1's Research tab).
        path: 'research',
        loadComponent: () =>
          import('./research/research.component').then(
            m => m.ResearchComponent
          ),
        data: { breadcrumb: { label: 'GNU_RESEARCH', translate: true } },
        canActivate: [PermissionGuard],
      },
      {
        // The native research page (SD-2716), for ALPHA-stage test accounts only.
        path: 'research-native',
        loadChildren: () =>
          import('@qtek/feature/research').then(m => m.researchRoutes),
        data: { breadcrumb: { label: 'GNU_RESEARCH_NATIVE', translate: true } },
        canActivate: [PermissionGuard],
      },
      {
        path: 'orgs',
        loadChildren: () =>
          import('@qtek/feature/organizations').then(
            m => m.organizationsRoutes
          ),
        data: { breadcrumb: { label: 'GNR_ALL_ACCOUNTS', translate: true } },
        canActivate: [PermissionGuard],
      },
      {
        path: 'docs/:docId',
        loadComponent: () =>
          import('@qtek/libs/doc-viewer').then(m => m.DocViewerComponent),
      },
      {
        path: 'upload',
        loadChildren: () =>
          import('@qtek/feature/file-uploader').then(m => m.FileUploaderModule),
      },
    ],
  },
  {
    path: 'error',
    loadChildren: () => import('@qtek/feature/error').then(m => m.ErrorModule),
    canActivate: [HideLoaderGuard],
  },
  {
    path: '**',
    loadChildren: () =>
      import('@qtek/feature/not-found').then(m => m.NotFoundModule),
    canActivate: [HideLoaderGuard],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
